/**
 * Plain calendar popover for the wizard date-step (027 US6 PR-2, FR-026;
 * restyled + portaled at the owner-residuals r1 pass, T066):
 * the trigger toggles a month grid; picking a day reports the ISO value up
 * and closes. Month and year selects jump straight to a month; a click
 * outside closes without picking. A deliberately PLAIN implementation — the
 * repo carries no calendar/popover dependency and new deps need a proposal
 * first.
 *
 * PORTAL (owner finding #6, the proto's approach): the panel portals to the
 * wizard DIALOG and positions absolutely against it, so it overlays the
 * pinned footer/progress area and the full month is always visible — the
 * scroll area's overflow cannot clip it. Portaling INTO the dialog (not
 * `document.body`) keeps Radix's modal focus trap satisfied (the panel's
 * day buttons are inside the trap) and keeps the Escape presence seam
 * (`[data-calendar-popover]` findable in the document) unchanged. The
 * position is a PURE decision (`calendarPlacement.ts`) made on open and
 * again when the dialog resizes: every month renders six week rows, so the
 * panel height never changes and month navigation cannot push it past the
 * dialog edge. While the panel is open a scroll of the step area does not
 * re-anchor it.
 *
 * The visible month follows the selected value when one exists (so a typed
 * date opens its own month) and the current month otherwise. Month names
 * and the Monday-first weekday header are Finnish-first i18n (the proto's
 * English calendar is its unlocalized default, not intent). Adjacent-month
 * overflow days render greyed AND pickable — the proto's outside days are
 * muted but interactive (react-day-picker defaults). Date VALIDATION stays
 * the engine's concern: no catalogued date field constrains a range, so the
 * proto's disabled-future rule is deliberately NOT adopted (a picker-only
 * restriction would make the picker stricter than the typed path).
 */

import { type JSX, type RefObject, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { MessageKeys } from '../../../i18n/messages/index.js';
import { useTranslate } from '../../../i18n/useTranslate.js';
import { CalendarIcon } from '../../../shell/icons.js';
import { CALENDAR_WEEK_ROWS, panelSize, resolvePortalPosition } from './calendarPlacement.js';
import { formatIsoToFinnish } from './dateText.js';

const triggerClasses =
  'absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center ' +
  'rounded-md text-muted-foreground transition-colors hover:text-primary ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
// overflow-y-auto: on a dialog shorter than the panel its height is capped
// and the bottom week rows scroll into reach.
const panelClasses =
  'absolute z-30 w-84 overflow-y-auto overscroll-contain rounded-xl border border-border bg-popover p-4 shadow-xl';
const monthRowClasses = 'flex items-center justify-between gap-0.5 pb-3';
const monthSelectGroupClasses = 'flex min-w-0 flex-1 items-center justify-center gap-0.5';
// text-base (16px): iOS Safari zooms the page into a smaller focused select.
// h-11 (44px) here and on the chevrons is the mobile tap-target minimum;
// h-8 would be 48px (tokens.css retunes it) and crowd the longest month names.
const selectClasses =
  'h-11 min-w-0 rounded-md border border-border bg-popover px-0.5 text-base font-semibold ' +
  'text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
// Circular bordered chevrons (the proto's calendar shot).
const monthNavButtonClasses =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border text-primary ' +
  'hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const weekdayRowClasses = 'grid grid-cols-7 gap-x-1 pb-1';
const weekdayCellClasses =
  'flex h-6 items-center justify-center text-xs font-medium text-muted-foreground';
// Compact row rhythm: tight vertical gap, regular horizontal gap.
const gridClasses = 'grid grid-cols-7 justify-items-center gap-x-1 gap-y-0.5';
// Day-cell states are FULL SIBLING VARIANTS, not appended overrides: cn()/
// template concat is plain string joining, so competing same-property classes
// (bg, hover:bg, border) would resolve by Tailwind compile order. Hover =
// subtle circle; SELECTED = filled primary ROUNDED-SQUARE with the dark
// brand ring (the proto shot); TODAY = outlined circle; OVERFLOW (adjacent
// month) = greyed but pickable, like the proto's outside days.
const dayButtonClasses =
  'inline-flex h-9 w-9 items-center justify-center rounded-full text-sm text-foreground ' +
  'transition-colors hover:bg-primary/10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const daySelectedClasses =
  'inline-flex h-9 w-9 items-center justify-center rounded-lg text-sm font-semibold ' +
  'bg-primary text-primary-foreground ring-2 ring-brand-deep ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const dayTodayClasses =
  'inline-flex h-9 w-9 items-center justify-center rounded-full text-sm text-foreground ' +
  'border-2 border-primary transition-colors hover:bg-primary/10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const dayOverflowClasses =
  'inline-flex h-9 w-9 items-center justify-center rounded-full text-sm text-muted-foreground/60 ' +
  'transition-colors hover:bg-primary/10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Finnish-first month-name keys, indexed by month-1 (12 fi keys + en parity). */
const MONTH_KEYS = [
  'compose.wizard.date.month.1',
  'compose.wizard.date.month.2',
  'compose.wizard.date.month.3',
  'compose.wizard.date.month.4',
  'compose.wizard.date.month.5',
  'compose.wizard.date.month.6',
  'compose.wizard.date.month.7',
  'compose.wizard.date.month.8',
  'compose.wizard.date.month.9',
  'compose.wizard.date.month.10',
  'compose.wizard.date.month.11',
  'compose.wizard.date.month.12',
] as const satisfies readonly MessageKeys[];

interface MonthView {
  readonly year: number;
  /** 1-12. */
  readonly month: number;
}

function monthViewFromIso(iso: string): MonthView | null {
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(iso);
  if (match === null) {
    return null;
  }
  return { year: Number(match[1]), month: Number(match[2]) };
}

function currentMonthView(): MonthView {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function shiftMonth(view: MonthView, delta: 1 | -1): MonthView {
  const shifted = new Date(Date.UTC(view.year, view.month - 1 + delta, 1));
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1 };
}

function daysInMonth(view: MonthView): number {
  return new Date(Date.UTC(view.year, view.month, 0)).getUTCDate();
}

/** Monday-first leading overflow count for the month's first day. */
function leadingOverflow(view: MonthView): number {
  const firstDay = new Date(Date.UTC(view.year, view.month - 1, 1)).getUTCDay();
  return (firstDay + 6) % 7;
}

/** How far back and ahead the year select reaches from the current year. */
const YEARS_BACK = 100;
const YEARS_AHEAD = 10;

/**
 * Year options: a window around this year, widened to include the shown
 * year so a typed date outside the window still has its option — the
 * picker must never refuse a date the typed path accepts.
 */
function yearOptions(shownYear: number): readonly number[] {
  const thisYear = new Date().getFullYear();
  const first = Math.min(thisYear - YEARS_BACK, shownYear);
  const last = Math.max(thisYear + YEARS_AHEAD, shownYear);
  return Array.from({ length: last - first + 1 }, (_, index) => first + index);
}

function toIso(view: MonthView, day: number): string {
  // Belt-and-braces 4-digit year (the view only ever derives from parse-
  // validated >= 1000 values or the current date, but the ISO shape must
  // hold regardless).
  return `${String(view.year).padStart(4, '0')}-${String(view.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Today's local date as the engine ISO — drives the outlined today state. */
function todayIso(): string {
  const now = new Date();
  return toIso({ year: now.getFullYear(), month: now.getMonth() + 1 }, now.getDate());
}

/** A renderable day cell: its month-view, day number and overflow flag. */
interface DayCell {
  readonly view: MonthView;
  readonly day: number;
  readonly overflow: boolean;
}

/**
 * The Monday-first cell run: leading + current + trailing overflow, padded
 * to a fixed six weeks so the panel height is the same in every month.
 */
function monthCells(view: MonthView): readonly DayCell[] {
  const cells: DayCell[] = [];
  const lead = leadingOverflow(view);
  const prev = shiftMonth(view, -1);
  const prevDays = daysInMonth(prev);
  for (let index = lead - 1; index >= 0; index -= 1) {
    cells.push({ view: prev, day: prevDays - index, overflow: true });
  }
  const dayCount = daysInMonth(view);
  for (let day = 1; day <= dayCount; day += 1) {
    cells.push({ view, day, overflow: false });
  }
  const next = shiftMonth(view, 1);
  const trailing = CALENDAR_WEEK_ROWS * 7 - cells.length;
  for (let day = 1; day <= trailing; day += 1) {
    cells.push({ view: next, day, overflow: true });
  }
  return cells;
}

interface PortalState {
  readonly target: HTMLElement;
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly maxHeight: number;
}

export interface CalendarPopoverProps {
  /** The field's current ISO value ('' when unset); pins the opening month. */
  readonly selectedIso: string;
  readonly onPick: (iso: string) => void;
  /**
   * Focus target after a successful PICK (the date input the value landed
   * in) — so Enter advances the step instead of re-activating the trigger
   * and re-opening the calendar. Escape/cancel closes still return focus to
   * the trigger (the member changed their mind; the trigger is where they
   * were).
   */
  readonly pickFocusRef?: RefObject<HTMLInputElement | null>;
}

export function CalendarPopover({
  selectedIso,
  onPick,
  pickFocusRef,
}: CalendarPopoverProps): JSX.Element {
  const t = useTranslate();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MonthView | null>(null);
  const [portal, setPortal] = useState<PortalState | null>(null);
  // Focus target on open (MED-2): the selected day when visible, else the
  // first day of the month. A ref + Effect because focus is a DOM side
  // effect of the open transition (R35).
  const initialFocusRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const activeView = view ?? monthViewFromIso(selectedIso) ?? currentMonthView();
  const cells = monthCells(activeView);

  // Portal target + position: measure the trigger against the wizard dialog
  // and let the pure decision pick the side/offsets. Geometry reads are a
  // DOM side effect of the open transition; in test DOMs every rect is zeros
  // and the position degrades to the host's corner inset. The dialog follows
  // the visual viewport, so it is re-measured when the dialog resizes (an iOS
  // keyboard closing after the calendar took focus).
  useEffect(() => {
    if (!open) {
      setPortal(null);
      return;
    }
    const trigger = triggerRef.current;
    const host = trigger?.closest<HTMLElement>('[role="dialog"]');
    if (trigger == null || host == null) {
      return;
    }
    const place = (): void => {
      const size = panelSize(
        Number.parseFloat(getComputedStyle(document.documentElement).fontSize),
      );
      const position = resolvePortalPosition({
        triggerRect: trigger.getBoundingClientRect(),
        boundsRect: host.getBoundingClientRect(),
        panelHeight: size.height,
        panelWidth: size.width,
      });
      setPortal({
        target: host,
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: position.maxHeight,
      });
    };
    place();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(place);
    observer.observe(host);
    return () => {
      observer.disconnect();
    };
  }, [open]);

  // Focus once per OPEN (not per month navigation): the portal mounts one
  // render after the open click, so the focus effect keys on its presence.
  const portalMounted = portal !== null;
  useEffect(() => {
    if (portalMounted) {
      initialFocusRef.current?.focus();
    }
  }, [portalMounted]);

  // Close-path focus contract: the popover took focus on open (MED-2), and
  // no close path may drop it on <body>. A successful PICK hands focus to
  // the date input via `pickFocusRef` so Enter advances the step rather than
  // re-activating the trigger and re-opening the calendar; Escape (a cancel)
  // returns focus to the trigger; an outside click leaves it on a control
  // that took it and otherwise returns it to the trigger. The Escape and
  // outside-click effects INLINE the close calls instead of sharing a
  // function: the exhaustive-deps rule rejects a per-render function
  // identity as a dep, and the compiler-uniformity rule forbids a manual
  // useCallback — setters and the ref are dependency-exempt.
  const closeAfterPick = (): void => {
    setOpen(false);
    setView(null);
    (pickFocusRef?.current ?? triggerRef.current)?.focus();
  };

  // Escape closes the popover REGARDLESS of focus position (the date input
  // keeps focus when the member types, so a panel-local handler would miss
  // it). The wizard Dialog's escape hook stands down whenever the popover is
  // present in the document, so this listener is the single close path.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        // Inlined close (see the close-path focus contract above).
        setOpen(false);
        setView(null);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  // A click or tap outside the panel and the trigger closes it without a
  // pick. By `click` time the press has already moved focus: a control that
  // took it (the date input, another button) keeps it; otherwise — a blank
  // area focuses the dialog or the heading, both tabIndex -1 — focus
  // returns to the trigger. The wizard dialog's own outside handling still
  // keeps the wizard open.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClick = (event: MouseEvent): void => {
      const target = event.target;
      const panelNode = panelRef.current;
      if (
        !(target instanceof Node) ||
        panelNode?.contains(target) === true ||
        triggerRef.current?.contains(target) === true
      ) {
        return;
      }
      const active = document.activeElement;
      const focusLost =
        !(active instanceof HTMLElement) ||
        active === document.body ||
        active.tabIndex < 0 ||
        panelNode?.contains(active) === true;
      setOpen(false);
      setView(null);
      if (focusLost) {
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [open]);

  const hasSelectionInView =
    monthViewFromIso(selectedIso) !== null &&
    toIso(activeView, 1).slice(0, 7) === selectedIso.slice(0, 7);

  const monthCaption = `${t(MONTH_KEYS[activeView.month - 1] ?? MONTH_KEYS[0])} ${activeView.year}`;

  const panel = (
    /* A labelled <section> (implicit region; NOT role=dialog — a nested
       dialog would misreport modality and break the dialog-topology seams)
       named by the month caption; the trigger declares it via
       aria-controls. */
    <section
      ref={panelRef}
      id={panelId}
      aria-label={monthCaption}
      className={panelClasses}
      style={
        portal === null
          ? undefined
          : {
              top: portal.top,
              left: portal.left,
              maxWidth: portal.width,
              maxHeight: portal.maxHeight,
            }
      }
      data-calendar-popover=""
    >
      {/* The selects do not announce a month the chevrons changed. */}
      <span className="sr-only" aria-live="polite">
        {monthCaption}
      </span>
      <div className={monthRowClasses}>
        <button
          type="button"
          aria-label={t('compose.wizard.date.previousMonth')}
          className={monthNavButtonClasses}
          onClick={() => setView(shiftMonth(activeView, -1))}
        >
          ‹
        </button>
        <div className={monthSelectGroupClasses}>
          <select
            aria-label={t('compose.wizard.date.monthSelect')}
            className={`${selectClasses} flex-1`}
            value={activeView.month}
            onChange={(event) =>
              setView({ year: activeView.year, month: Number(event.target.value) })
            }
          >
            {MONTH_KEYS.map((key, index) => (
              <option key={key} value={index + 1}>
                {t(key)}
              </option>
            ))}
          </select>
          <select
            aria-label={t('compose.wizard.date.yearSelect')}
            className={`${selectClasses} shrink-0`}
            value={activeView.year}
            onChange={(event) =>
              setView({ year: Number(event.target.value), month: activeView.month })
            }
          >
            {yearOptions(activeView.year).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          aria-label={t('compose.wizard.date.nextMonth')}
          className={monthNavButtonClasses}
          onClick={() => setView(shiftMonth(activeView, 1))}
        >
          ›
        </button>
      </div>
      {/* Monday-first weekday header; presentational — the day buttons
          carry full Finnish dates as their accessible names. */}
      <div className={weekdayRowClasses} aria-hidden="true">
        {t('compose.wizard.date.weekdays')
          .split(' ')
          .map((weekday) => (
            <span key={weekday} className={weekdayCellClasses}>
              {weekday}
            </span>
          ))}
      </div>
      <div className={gridClasses}>
        {cells.map((cell) => {
          const iso = toIso(cell.view, cell.day);
          const selected = iso === selectedIso;
          const isInitialFocus =
            selected || (!hasSelectionInView && !cell.overflow && cell.day === 1);
          const stateClasses = selected
            ? daySelectedClasses
            : cell.overflow
              ? dayOverflowClasses
              : iso === todayIso()
                ? dayTodayClasses
                : dayButtonClasses;
          return (
            <button
              key={iso}
              ref={isInitialFocus ? initialFocusRef : undefined}
              type="button"
              aria-label={formatIsoToFinnish(iso)}
              aria-pressed={selected}
              {...(cell.overflow ? { 'data-overflow-day': '' } : {})}
              className={stateClasses}
              onClick={() => {
                onPick(iso);
                closeAfterPick();
              }}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </section>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={t('compose.wizard.date.openCalendar')}
        aria-expanded={open}
        aria-controls={panelId}
        className={triggerClasses}
        onClick={() => setOpen((current) => !current)}
      >
        <CalendarIcon aria-hidden="true" className="h-5 w-5" />
      </button>
      {open && portal !== null ? createPortal(panel, portal.target) : null}
    </>
  );
}
